import React, { useCallback } from 'react';
import styled from 'styled-components';

import { useQueryParams } from 'src/hooks';
import { Button, Notification } from 'src/UIComponents';
import GoogleLogo from 'src/assets/btn_google.svg';
import FacebookLogo from 'src/assets/btn_facebook.svg';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.N30};
`;

const LoginBox = styled.div`
  min-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: ${({ theme }) => theme.colors.N20};
  border: 3px solid ${({ theme }) => theme.colors.N80};
  border-radius: 8px;
  box-shadow: rgba(50, 50, 93, 0.25) 0px 50px 100px -20px,
    rgba(0, 0, 0, 0.3) 0px 30px 60px -30px;
  padding: ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.lg};
`;

const BrandButton = styled(Button).attrs({
  color: 'info',
  outline: true,
})`
  justify-content: flex-start;
  font-size: 14px;
  font-family: 'Roboto', 'Open Sans', 'Helvetica', Helvetica, Arial, sans-serif;
  padding: 1em;
  background-color: ${({ theme }) =>
    theme.mode === 'dark' && theme.colors.N100};

  &:focus {
    color: black; // Black since buttons have white background in both dark and light mode.
  }
  &:hover {
    background-color: ${({ theme }) =>
      theme.mode === 'dark' ? theme.colors.N70 : theme.colors.N30};
  }
`;
const BrandIcon = styled.img`
  width: 2em;
  height: 2em;
  margin-right: 1.4em;
`;

// https://developers.google.com/identity/branding-guidelines#padding
const GoogleButton = styled(BrandButton)`
  text-transform: uppercase;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.54);

  &:hover {
    color: ${({ theme }) => theme.colors.google};
  }
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.google};
  }
  &:active {
    background-color: ${({ theme }) => theme.colors.google};
    color: #fff;
  }
`;

// https://www.facebook.com/brand/resources/facebookapp/logo
const FacebookButton = styled(BrandButton)`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.facebook};

  &:hover {
    color: ${({ theme }) => theme.colors.facebook};
  }
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.facebook};
  }
  &:active {
    background-color: ${({ theme }) => theme.colors.facebook};
    color: #fff;
  }
`;

function LoginView({ ...rest }) {
  const query = useQueryParams();

  const startOAuth = useCallback(
    (provider) => {
      let url = `/auth/${provider}`;

      if (query.has('redirect') && query.has('redirect') !== '/login') {
        url += `?redirect=${query.get('redirect')}`;
      }

      window.location.assign(url); // Do not use history.push since this is a Backend Route only.
    },
    [query]
  );

  return (
    <Container {...rest}>
      {query.get('success') === '0' && (
        <Notification $color="danger">Något gick fel :/</Notification>
      )}
      {query.get('loggedout') === '1' && (
        <Notification $color="success">Du är utloggad.</Notification>
      )}

      <LoginBox>
        <h1 style={{ alignSelf: 'center' }}>Login</h1>

        <GoogleButton
          tabIndex="1"
          onClick={() => startOAuth('google')}
          className="mb-3"
        >
          <BrandIcon src={GoogleLogo} alt="Google logo" />
          Sign in with Google
        </GoogleButton>
        <FacebookButton tabIndex="1" onClick={() => startOAuth('facebook')}>
          <BrandIcon src={FacebookLogo} alt="Facebook logo" />
          Login with Facebook
        </FacebookButton>
      </LoginBox>
    </Container>
  );
}

export default LoginView;
